// @givmo/org-console — the ACCESS EDITOR: one admin's org tier and their app-by-app access.
//
// The console's reason to exist. Everything else here reports; this decides.
//
// ⚠ ACCESS IS INVITATION-BASED (D-CPO-24): a Global Admin holds every app by tier, and anyone else
// holds exactly the apps they were invited into or granted here. An app shown off is one the person
// cannot open, so every control reads as what it is — a grant, or its removal.
//
// ⚠ A DEMOTION IS A CHOICE (OM-57, D-CPO-64's Also). Turning Global Admin off shows every app OFF,
// nothing pre-checked, and Save stays off until at least one is turned on; the save is the role
// change (`POST …/role-change`), which writes exactly the apps turned on among those shown and keeps
// nothing implicitly among them. An app this dialog never showed (hidden by the backend, or left out
// by the served set and named to the server as `shown_modules`) keeps the person's stored row, at
// Member (OM-95). Everything else this dialog saves is the PUT.
//
// THE WIRE SHAPE IS NOT COMPUTED HERE. `org-access-model.ts` owns the draft, the diff that becomes
// the PUT — including the three-valued `permissions` and the grant-and-narrow single request — and
// the demotion's choice. This file is the form: it renders the draft, edits it, and hands the write
// to its mutation.
//
// TWO PUT ANSWERS GET CONSIDERED COPY RATHER THAN A RAW MESSAGE, and both are detected on the CODE,
// never on the server's English:
//   * **422 UNKNOWN_PERMISSION_KEY** names the offending keys and the whole write did nothing. It is
//     unreachable through these controls (every checkbox comes from the catalog the same request is
//     validated against), so seeing it means the catalog moved under an open dialog — exactly when
//     naming the keys beats a generic failure.
//   * **422 LAST_MODULE_REVOKE_REFUSED** is NEW on this surface and has no twin anywhere else. An
//     admin with zero stored access facts reads back as never enrolled and falls through to a WIDER
//     default set than the configuration that just narrowed them, so the revoke is refused. The copy
//     has to name the alternative or the refusal reads as a bug.
//
// THE ROLE CHANGE'S ANSWERS are read by `roleChangeRefusal`: its `ROLE_CHANGE_*` codes, and on a
// self-demotion only, the class writer's untranslated bare 400 for the last Global Admin. Each says
// the ruled words of `org-demotion-copy.ts`, and leaves Save in the state a retry deserves: off
// until the dialog is reopened after the 409 and the last Global Admin's refusal (no choice of apps
// fixes either), off until the choice changes after a refusal of the choice itself, and on after
// anything else.
//
// THE EXTERNAL MARK (§OM-53) is a label about the person, set by the switch at the editor's foot and
// shown beside their name in the title, live with the switch. It rides the PUT; a Save that also
// demotes sends the role change first and the mark's own PUT once that lands, so every refusal of
// the role change still says, truly, that nothing was saved.
//
// ⚠ IT ALSO REMOVES PEOPLE FROM THE TEAM, and that is a DIFFERENT act from everything above, not a
// harder version of it. Every control above narrows what somebody can reach while they stay on the
// team — the backend in fact refuses the patch that would leave them with nothing. "Remove from
// team" ends the membership: their grants and custom sets go with it, so adding them back starts
// from nothing. The last-module refusal's copy has always pointed at this act; until OM-10 leg 4
// there was nowhere on this surface to perform it, and the page carried a link to a class module
// instead — the sentence the 2026-09-01 ruling called unpublishable.
//
// Its own refusal is a further considered answer, and it reads the CODE where the role change's twin
// reads who the write was for: a **422 LAST_GLOBAL_ADMIN_REFUSED**. The console translates each
// class writer's own last-Owner guard into that one typed answer on this path, so this branch is
// class-independent.
//
// DATA CONTRACT: the `<prefix>-access-editor-*` test-id family, including `-global-admin`,
// `-global-admin-notice`, `-self-demote-warning`, `-only-global-admin`, `-choose-apps`,
// `-module-<slug>` (and its `-access` and `-tier`), `-title-external`, `-external` (and its
// `-section` and `-switch`), `-error`, `-save`, `-cancel`, `-remove`, `-remove-confirm`,
// `-remove-consequences`, `-remove-cancel`, `-remove-confirm-button` and `-remove-error`.

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  ErrorMessage,
  Select,
  Switch,
  testIdProp,
} from '@givmo/design-system';
import { isServedModule, testIdPrefix, type OrgConsoleConfig } from './org-console-config';
import {
  buildAccessPatch,
  buildExternalMarkPatch,
  buildRoleChange,
  catalogFor,
  initialDraft,
  isCustomizable,
  isDemotion,
  isLastGlobalAdminRemovalRefusal,
  isLastModuleRevokeRefusal,
  isOnlyGlobalAdmin,
  keysAtLevel,
  LEVEL_MEMBER,
  LEVEL_OWNER,
  roleChangeRefusal,
  sameRoleChange,
  tierDefaults,
  unknownPermissionKeys,
  type AccessDraft,
  type ModuleDraft,
} from './org-access-model';
import { orgMemberEmail, orgMemberName } from './org-decode';
import { DEMOTION_COPY } from './org-demotion-copy';
import { ExternalNote, ExternalSection } from './org-external';
import { LevelGives } from './org-level-gives';
import {
  GLOBAL_ADMIN_LABEL,
  MEMBER_LABEL,
  ROLE_MEMBER,
  ROLE_OWNER,
  globalAdminDescription,
  offForOrgLabel,
  type OrgTier,
} from './org-tiers';
import {
  useChangeOrgMemberRole,
  useLeaveOrganization,
  useOrgAccessCatalog,
  useOrgMembers,
  useRemoveOrgMember,
  useUpdateOrgMemberAccess,
} from './use-org-console';
import type {
  OrgConsoleMemberRow,
  OrgModuleCatalogRow,
  OrgRoleChangeBody,
  UpdateOrgMemberAccessBody,
} from './wire-types';

export interface OrgAccessEditorDialogProps {
  config: OrgConsoleConfig;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The admin being edited; `null` when closed. */
  member: OrgConsoleMemberRow | null;
  /** The signed-in admin's own backend user id, so the dialog can warn about self-demotion. */
  selfUserId: string | null;
  /** Hands back the row the write answered with, so the caller re-renders from the SERVER's answer. */
  onSaved: (updated: OrgConsoleMemberRow) => void;
  /**
   * May the viewer take this person OFF the team?
   *
   * ⚠ **Explicit, and defaulting to `false`, because this is the destructive one.** Every other
   * control in this dialog is already behind the caller's own gate — the dialog only opens from a
   * roster row a Global Admin clicked — but a capability a surface merely INHERITS is a capability
   * the next mount forgets it has. Passing it makes the gate visible at the boundary, and a host
   * that mounts this editor for a non-Global-Admin renders no removal control at all rather than a
   * dead one the server refuses.
   */
  canRemove?: boolean;
  /** Fired after a removal lands, so the caller can close the row it was opened from. */
  onRemoved?: (removedUserId: string) => void;
  /**
   * The host's sign-out and door hop, called after a removal of the SIGNED-IN person lands
   * (EP-MNT9 (c)): someone who has taken themselves off the team must not be left on a console that
   * can no longer read their organization. Absent ⇒ the self-removal only closes the dialog, as
   * before, and its words promise no sign-out.
   */
  onLeft?: () => void;
}

export function OrgAccessEditorDialog({
  config,
  open,
  onOpenChange,
  member,
  selfUserId,
  onSaved,
  canRemove = false,
  onRemoved,
  onLeft,
}: OrgAccessEditorDialogProps) {
  const prefix = `${testIdPrefix(config)}-access-editor`;
  const catalogQuery = useOrgAccessCatalog(config.orgRef, open);
  // ⚠ ONLY THE APPS PEOPLE CAN OPEN HERE (OM-95). An app left out is never written: the PUT is a
  // per-app diff over the rows shown, so a grant for an app this console does not serve stays as it
  // is, and a demotion's choice is made among the apps that can be opened.
  const catalog = useMemo(
    () => (catalogQuery.data ?? []).filter((row) => isServedModule(config, row.module)),
    [catalogQuery.data, config],
  );
  // The apps this dialog drew, named to a demotion only when the served set left one out: the server
  // then keeps the person's stored rows for the apps this build never showed (OM-95, D-CPO-77).
  const shownModules = useMemo(
    () =>
      catalog.length < (catalogQuery.data ?? []).length
        ? catalog.map((row) => row.module)
        : undefined,
    [catalog, catalogQuery.data],
  );
  const save = useUpdateOrgMemberAccess(config.orgRef, config.onAccessWritten);
  const changeRole = useChangeOrgMemberRole(config.orgRef, config.onAccessWritten);
  const remove = useRemoveOrgMember(config.orgRef, config.onAccessWritten);
  // The same act aimed at the signed-in person, when the host signs them out after: no cache work
  // (see `confirmRemoval`).
  const leave = useLeaveOrganization(config.orgRef);
  // The roster this page has loaded (the panel's own read, from the cache): whether anyone else is a
  // Global Admin, for the last one's line below, which it reads afresh before saying.
  const roster = useOrgMembers(config.orgRef);

  const [draft, setDraft] = useState<AccessDraft | null>(null);
  const [baseline, setBaseline] = useState<AccessDraft | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [unknownKeys, setUnknownKeys] = useState<string[]>([]);
  const [lastModuleRefused, setLastModuleRefused] = useState(false);
  // ⚠ A ROLE-CHANGE REFUSAL A RETRY CANNOT FIX keeps Save off (OM-57 web). After the 409 the person's
  // standing moved under this dialog, and after the last Global Admin's refusal no choice of apps can
  // succeed, so Save stays off until the dialog is reopened. After a refusal of the choice itself —
  // an app withdrawn, no app — the same choice would be refused again, so Save returns once it changes.
  const [saveOffUntilReopen, setSaveOffUntilReopen] = useState(false);
  const [refusedChoice, setRefusedChoice] = useState<OrgRoleChangeBody | null>(null);
  // When this opening asked for the roster afresh, for the last Global Admin's line (see
  // `rosterSaysOnly` below); `null` until it has.
  const [rosterAskedAt, setRosterAskedAt] = useState<number | null>(null);
  // The removal's confirm step, as a MODE of this dialog rather than a nested one — see
  // `confirmingRemoval` below.
  const [confirmingRemoval, setConfirmingRemoval] = useState(false);
  const [removalError, setRemovalError] = useState<string | null>(null);

  // The admin the open dialog's draft was primed for; `null` while it is closed.
  const primedFor = useRef<OrgConsoleMemberRow | null>(null);

  // Re-prime whenever the dialog opens on an admin, and again when the catalog lands — it decides
  // which modules exist at all, so a draft built before it arrives has no rows to edit.
  //
  // ⚠ A CATALOG THAT LANDS ON AN OPEN DIALOG RE-PRIMES THE MODULE ROWS AND NOTHING ELSE (EP-MNT9-FIX2,
  // Org Machinery OM-52). On a slow network a person acts before it lands: flips the tier, arms the
  // removal's confirm, meets its refusal. Resetting those under them disarmed the confirm mid-question
  // and dropped a 422's sentence. So only a new opening starts afresh; the same opening's catalog
  // rebuilds the draft's and the baseline's module rows from it and keeps the tier the person chose,
  // the confirm step and every refusal as they were.
  useEffect(() => {
    if (!open || member === null) {
      primedFor.current = null;
      return;
    }
    const next = initialDraft(member, catalog);
    setBaseline(next);
    if (primedFor.current === member) {
      setDraft((current) => (current === null ? next : { ...current, modules: next.modules }));
      return;
    }
    primedFor.current = member;
    setDraft(next);
    setServerError(null);
    setUnknownKeys([]);
    setLastModuleRefused(false);
    setSaveOffUntilReopen(false);
    setRefusedChoice(null);
    setRosterAskedAt(null);
    // ⚠ RESET THE CONFIRM STEP TOO. Leaving it armed would reopen the dialog on the confirmation for
    // whoever was clicked NEXT — a destructive question about the wrong person, already answered.
    setConfirmingRemoval(false);
    setRemovalError(null);
  }, [open, member, catalog]);

  const patch = useMemo(
    () => (draft !== null && baseline !== null ? buildAccessPatch(draft, baseline) : null),
    [draft, baseline],
  );
  // The demotion's choice: `null` until at least one app is on, which is what keeps Save off.
  const roleChange = useMemo(
    () =>
      draft !== null && baseline !== null ? buildRoleChange(draft, baseline, shownModules) : null,
    [draft, baseline, shownModules],
  );

  // ⚠ A STALE ROSTER NEVER HOLDS BACK A LEGAL DEMOTION. The roster this page has loaded can be out of
  // date — someone may have been made a Global Admin in another tab or another session since — and the
  // hosts keep a read fresh for 30 s and never re-read it on focus. So when it says the person turning
  // their own Global Admin off is the only one, the editor reads it afresh, once per opening, and the
  // last one's line holds Save off only on an answer that came back after that ask and agrees. A read
  // that fails never blocks: the server's last-Owner guard is the backstop.
  const demotingOwnRow =
    member !== null &&
    draft !== null &&
    baseline !== null &&
    selfUserId !== null &&
    selfUserId.length > 0 &&
    member.user_id === selfUserId &&
    isDemotion(draft, baseline);
  const rosterSaysOnly =
    demotingOwnRow &&
    member !== null &&
    isOnlyGlobalAdmin(roster.data ?? [], member.user_id, roster.isSuccess && !roster.hasNextPage);
  const refetchRoster = roster.refetch;
  useEffect(() => {
    if (!rosterSaysOnly || rosterAskedAt !== null) return;
    setRosterAskedAt(Date.now());
    void refetchRoster();
  }, [rosterSaysOnly, rosterAskedAt, refetchRoster]);

  if (member === null || draft === null) return null;

  const who = orgMemberName(member);
  const email = orgMemberEmail(member);
  const isSelf = selfUserId !== null && selfUserId.length > 0 && member.user_id === selfUserId;
  const demoting = baseline !== null && isDemotion(draft, baseline);
  const demotingSelf = isSelf && demoting;
  const saving = save.isPending || changeRole.isPending;
  // Save after a refusal a retry cannot fix — see `saveOffUntilReopen` above.
  const retryRefused =
    saveOffUntilReopen ||
    (demoting && refusedChoice !== null && sameRoleChange(roleChange, refusedChoice));
  // The last Global Admin, said before a doomed save: only when the roster, read afresh for this act,
  // is whole and holds no one else at that tier (`dataUpdatedAt` is when the cache last took an
  // answer). Otherwise the server's last-Owner guard is the backstop.
  const rosterFresh =
    rosterAskedAt !== null && roster.dataUpdatedAt >= rosterAskedAt && !roster.isFetching;
  const onlyGlobalAdmin = rosterSaysOnly && rosterFresh;
  // While that read is out, nothing is said and nothing is offered: not the last one's line, and not
  // the warning and the apps it would replace.
  const checkingRoster =
    rosterSaysOnly && !rosterFresh && (rosterAskedAt === null || roster.isFetching);
  // Removing yourself signs you out where the host wires it (EP-MNT9 (c)), and the words say so.
  const signsOut = isSelf && onLeft !== undefined;
  const removing = remove.isPending || leave.isPending;

  const confirmRemoval = () => {
    setRemovalError(null);
    const outcome = {
      onSuccess: () => {
        onRemoved?.(member.user_id);
        onOpenChange(false);
        if (signsOut) onLeft?.();
      },
      onError: (error: unknown) => {
        // The ONE refusal worth its own sentence: the organization would be left with nobody who
        // can administer it. Everything else is a generic failure, because there is nothing
        // specific a person could do about it from here.
        setRemovalError(
          isLastGlobalAdminRemovalRefusal(error)
            ? `Your ${config.vocab.orgNoun} must always have at least one ${GLOBAL_ADMIN_LABEL}. Make someone else a ${GLOBAL_ADMIN_LABEL} first.`
            : 'Something went wrong. Please try again.',
        );
      },
    };
    // ⚠ A removal that signs the person out does NO cache work, as *Leave organization* does not
    // (`useLeaveOrganization`). The ordinary removal re-reads the roster, the caller's own standing
    // and, through `onAccessWritten`, the host's standing read — and every one of those now answers
    // that this person administers nothing. Asked before the sign-out lands (a host may release a
    // session of its own first, a network round trip), that answer can move them on its own: a
    // host's no-access branch may navigate, one of them to its registration screen. The host's
    // sign-out clears every read anyway.
    if (signsOut) {
      leave.mutate({ selfUserId: member.user_id }, outcome);
    } else {
      remove.mutate({ memberId: member.user_id }, outcome);
    }
  };

  const setModule = (module: string, next: Partial<ModuleDraft>) =>
    setDraft((current) =>
      current === null
        ? current
        : {
            ...current,
            modules: current.modules.map((row) =>
              row.module === module ? { ...row, ...next } : row,
            ),
          },
    );

  // The mark's own PUT, after a demotion's role change has landed (`buildExternalMarkPatch`). On a
  // failure the demotion stands and the mark does not: the roster takes the role change's row, and
  // so does this dialog, as its new starting point, keeping the mark the person chose. Save stays on,
  // and a retry is the mark's PUT alone. ⚠ NEVER THE ROLE CHANGE AGAIN: the person is no longer a
  // Global Admin, so a retry with a changed choice of apps would meet the server's 409 for that, and
  // say that someone else had changed them when this dialog had.
  const saveMarkAfterRoleChange = (
    body: UpdateOrgMemberAccessBody,
    demoted: OrgConsoleMemberRow | null,
  ) => {
    save.mutate(
      { targetUserId: member.user_id, body },
      {
        onSuccess: (updated) => {
          const row = updated ?? demoted;
          if (row !== null) onSaved(row);
          onOpenChange(false);
        },
        onError: () => {
          if (demoted !== null) {
            onSaved(demoted);
            const landed = initialDraft(demoted, catalog);
            setBaseline(landed);
            setDraft((current) => ({ ...landed, external: current?.external ?? landed.external }));
          }
          setServerError('Something went wrong. Please try again.');
        },
      },
    );
  };

  // ⚠ THE DEMOTION'S ONE WRITE, and never a PUT behind it on failure: the PUT's demotion keeps apps
  // implicitly, which is what the ruling forbids. A refusal leaves the dialog open on what was chosen.
  // A moved External mark follows it as its own PUT, only once it has landed.
  const submitRoleChange = (body: NonNullable<typeof roleChange>) => {
    const markPatch = baseline === null ? null : buildExternalMarkPatch(draft, baseline);
    changeRole.mutate(
      { targetUserId: member.user_id, body },
      {
        onSuccess: (updated) => {
          if (markPatch !== null) {
            saveMarkAfterRoleChange(markPatch, updated);
            return;
          }
          // The SERVER's row first, then close — as the PUT does below.
          if (updated !== null) onSaved(updated);
          onOpenChange(false);
        },
        onError: (error: unknown) => {
          // A demotion is the only write this sends, so "demoting self" is whether it is their row.
          const refusal = roleChangeRefusal(error, { demotingSelf: isSelf });
          switch (refusal?.kind) {
            case 'last-global-admin':
              setSaveOffUntilReopen(true);
              setServerError(DEMOTION_COPY.onlyGlobalAdmin);
              return;
            case 'not-global-admin':
              setSaveOffUntilReopen(true);
              setServerError(DEMOTION_COPY.changedElsewhere(who));
              return;
            case 'app-unavailable':
              setRefusedChoice(body);
              setServerError(
                DEMOTION_COPY.appWithdrawn(
                  refusal.module !== null ? config.vocab.moduleLabel(refusal.module) : null,
                  config.vocab.orgNoun,
                ),
              );
              return;
            case 'choice-required':
              setRefusedChoice(body);
              setServerError(DEMOTION_COPY.choiceRequired);
              return;
            default:
              // `malformed`, the route miss of a backend without the route, anything else: a retry
              // may work, so Save stays on.
              setServerError('Something went wrong. Please try again.');
          }
        },
      },
    );
  };

  const submit = () => {
    setServerError(null);
    setUnknownKeys([]);
    setLastModuleRefused(false);
    if (demoting) {
      if (roleChange !== null) submitRoleChange(roleChange);
      return;
    }
    if (patch === null) return;
    save.mutate(
      { targetUserId: member.user_id, body: patch },
      {
        onSuccess: (updated) => {
          // Consume the SERVER's row FIRST — the caller re-renders from it rather than from what this
          // dialog believed it was submitting — then close. The invalidation the hook fires is scoped
          // and deliberately not awaited, so the close never waits on unrelated reads.
          if (updated !== null) onSaved(updated);
          onOpenChange(false);
        },
        onError: (error: unknown) => {
          const keys = unknownPermissionKeys(error);
          if (keys !== null) {
            setUnknownKeys(keys);
            return;
          }
          if (isLastModuleRevokeRefusal(error)) {
            setLastModuleRefused(true);
            return;
          }
          setServerError('Something went wrong. Please try again.');
        },
      },
    );
  };

  // ══ THE CONFIRM STEP IS A MODE OF THIS DIALOG, NOT A SECOND ONE ═══════════════════════════════
  //
  // The obvious shape — a nested `Dialog.Root` over the editor — puts one focus trap inside another,
  // and the estate's own remove-confirmation precedent sidesteps that only because the PAGE owns both
  // dialogs and never has them open at once. This control lives INSIDE the editor, so the same escape
  // is not available. Swapping the body is: one dialog, one trap, and the person sees exactly one
  // question with nothing else on screen to act on — which is what a confirmation is for.
  if (confirmingRemoval) {
    return (
      <Dialog.Root open={open} onOpenChange={(next) => !removing && onOpenChange(next)}>
        <Dialog.Content
          aria-label={`Remove ${who} from this ${config.vocab.orgNoun}`}
          className="flex flex-col gap-16"
          testId={`${prefix}-remove-confirm`}
        >
          <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
            {isSelf
              ? `Remove yourself from this ${config.vocab.orgNoun}?`
              : `Remove ${who} from this ${config.vocab.orgNoun}?`}
          </Dialog.Title>
          {/* The consequences, named rather than implied. The apps line is the one a Global Admin is
              most likely to have wrong: this is not the access editor doing the same thing harder —
              their access goes with the membership, and re-adding them starts from nothing. */}
          <p
            className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
            {...testIdProp(`${prefix}-remove-consequences`)}
          >
            {signsOut
              ? `You will no longer be able to manage this ${config.vocab.orgNoun}'s team or its apps, and your access to every app here ends. You will be signed out. Someone else with ${GLOBAL_ADMIN_LABEL} access would have to add you back.`
              : isSelf
                ? `You will no longer be able to manage this ${config.vocab.orgNoun}'s team or its apps, and your access to every app here ends. Someone else with ${GLOBAL_ADMIN_LABEL} access would have to add you back.`
                : `They lose access to every app in this ${config.vocab.orgNoun}. Their app-by-app settings are cleared, so adding them back starts from scratch.`}
          </p>
          {/* An outstanding INVITE is not touched by a removal, and saying so here is what stops the
              next question. It is only worth a sentence for someone removing themselves, who would
              otherwise not know a way back in exists. */}
          {isSelf && (
            <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary">
              Any outstanding invitation to your address still works.
            </p>
          )}
          {removalError !== null && (
            <ErrorMessage testId={`${prefix}-remove-error`}>{removalError}</ErrorMessage>
          )}
          <div className="mt-8 flex items-center justify-end gap-8">
            <Button
              appearance="saas"
              tone="secondary"
              size="md"
              disabled={removing}
              // Back to the editor rather than closing the dialog: the person came here to change
              // access and may still mean to.
              onClick={() => setConfirmingRemoval(false)}
              testId={`${prefix}-remove-cancel`}
            >
              Cancel
            </Button>
            <Button
              appearance="saas"
              tone="danger"
              size="md"
              loading={removing}
              onClick={confirmRemoval}
              testId={`${prefix}-remove-confirm-button`}
            >
              {signsOut ? 'Remove and sign out' : isSelf ? 'Remove myself' : 'Remove from team'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Root>
    );
  }

  return (
    <Dialog.Root open={open} onOpenChange={(next) => !saving && onOpenChange(next)}>
      <Dialog.Content
        aria-label={`Manage access for ${who}`}
        className="flex max-h-[80vh] flex-col gap-16 overflow-y-auto"
        testId={`${prefix}-dialog`}
      >
        <div>
          {/* The External note shows where the person is named, live with its switch at the foot:
              whoever opens the editor sees at once whether this person is marked, without scrolling.
              The title is inline so the note follows its last word: a long name wraps, and the note
              stays after it rather than starting a line of its own. */}
          <div>
            <Dialog.Title className="inline font-sans text-s18 font-w700 tracking-givmo text-text-strong">
              Access for {who}
            </Dialog.Title>
            {draft.external && (
              <span className="ml-8 inline-block">
                <ExternalNote testId={`${prefix}-title-external`} />
              </span>
            )}
          </div>
          {/* ⚠ THE FULL ADDRESS LIVES HERE, and on a phone this is the only place it does. The
              roster's own email cell ellipsizes at 375 and harder at 320/200% text, so without it a
              person could not read the whole address anywhere on the phone layout — and this dialog
              is exactly where they have come to confirm WHO they are about to change. It wraps
              rather than truncating, because a half-shown address in the place of record is worse
              than a two-line one. */}
          {email.length > 0 && (
            <p
              className="mt-2 font-sans text-s13 font-w400 tracking-givmo break-all text-text-tertiary"
              {...testIdProp(`${prefix}-email`)}
            >
              {email}
            </p>
          )}
        </div>

        {/* ── The org-wide tier ─────────────────────────────────────────────────────────────── */}
        <div className="flex items-start justify-between gap-16 rounded-lg border border-border-subtle p-16">
          <div className="min-w-0">
            <p className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
              {GLOBAL_ADMIN_LABEL}
            </p>
            <p className="mt-2 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary">
              {globalAdminDescription(config.vocab.orgNoun)}
            </p>
          </div>
          <Switch
            appearance="saas"
            checked={draft.isGlobalAdmin}
            onCheckedChange={(checked) =>
              setDraft((current) =>
                current === null ? current : { ...current, isGlobalAdmin: checked },
              )
            }
            aria-label={GLOBAL_ADMIN_LABEL}
            testId={`${prefix}-global-admin`}
          />
        </div>

        {/* A warning, not a block, while another Global Admin is known or may exist: the server's
            last-Owner guard is the backstop. The last one's own case is said in the list's place,
            and while the roster is being read afresh for it, neither shows. */}
        {demotingSelf && !onlyGlobalAdmin && !checkingRoster && (
          <p
            className="font-sans text-s13 font-w500 leading-h15 tracking-givmo text-text-secondary"
            {...testIdProp(`${prefix}-self-demote-warning`)}
          >
            {DEMOTION_COPY.selfWarning(config.vocab.orgNoun)}
          </p>
        )}

        {/* ── Per-module access ────────────────────────────────────────────────────────────── */}
        {draft.isGlobalAdmin ? (
          <p
            className="rounded-lg bg-surface-sunken p-16 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
            {...testIdProp(`${prefix}-global-admin-notice`)}
          >
            A {GLOBAL_ADMIN_LABEL} has every app, at {ROLE_OWNER} level. Turn {GLOBAL_ADMIN_LABEL}{' '}
            off to set access app by app.
          </p>
        ) : onlyGlobalAdmin ? (
          // The roster is whole and nobody else is a Global Admin: no choice of apps can save, so the
          // list is not offered at all, and the line says the way out.
          <p
            className="rounded-lg bg-surface-sunken p-16 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
            {...testIdProp(`${prefix}-only-global-admin`)}
          >
            {DEMOTION_COPY.onlyGlobalAdmin}
          </p>
        ) : checkingRoster ? null : (
          <div className="flex flex-col gap-12">
            {/* ⚠ A DEMOTION STARTS FROM NOTHING HELD (D-CPO-64's Also), so the list opens all off
                and says what to do with it: the apps turned on are the apps they'll have, and Save
                stays off until one is. The line goes once a regular admin's editor is opened
                again — their apps are then simply theirs. */}
            {demoting && (
              <p
                className="font-sans text-s13 font-w500 leading-h15 tracking-givmo text-text-secondary"
                {...testIdProp(`${prefix}-choose-apps`)}
              >
                {DEMOTION_COPY.chooseApps(isSelf)}
              </p>
            )}
            {draft.modules.map((module) => (
              <ModuleSection
                key={module.module}
                config={config}
                idPrefix={`${prefix}-module-${module.module}`}
                module={module}
                entry={catalogFor(catalog, module.module)}
                enabledForOrg={
                  member.modules.find((row) => row.module === module.module)?.enabled_for_org !==
                  false
                }
                // The role change grants each app at its level's defaults; a custom set is made
                // afterwards, in this same editor, once they are a regular admin.
                customizeOffered={!demoting}
                onChange={(next) => setModule(module.module, next)}
              />
            ))}
            {draft.modules.length === 0 && (
              <p
                className="font-sans text-s13 text-text-tertiary"
                {...testIdProp(`${prefix}-no-modules`)}
              >
                {catalogQuery.isPending ? 'Loading your apps…' : 'No apps to configure.'}
              </p>
            )}
          </div>
        )}

        {/* ── About this person: the External mark, after the access ──────────────────────────
            A label about the person that changes nothing they can do, so it follows the controls
            that do, under a label of its own: the editor still opens on someone's access, and the
            label sits at the foot for the people who will never carry it (the title says whether
            they do). Before the refusals, so a refusal stays beside the buttons. Independent of the
            tier: a Global Admin can carry it. */}
        <ExternalSection
          orgNoun={config.vocab.orgNoun}
          checked={draft.external}
          onCheckedChange={(checked) =>
            setDraft((current) => (current === null ? current : { ...current, external: checked }))
          }
          testId={`${prefix}-external`}
        />

        {unknownKeys.length > 0 && (
          <ErrorMessage testId={`${prefix}-unknown-keys`}>
            {`Your apps changed while this was open, so nothing was saved. These permissions no longer exist: ${unknownKeys.join(', ')}. Close and reopen to start from the current list.`}
          </ErrorMessage>
        )}
        {lastModuleRefused && (
          <ErrorMessage testId={`${prefix}-last-module`}>
            {`Someone has to keep at least one app, so this was not saved. To take everything away, turn off their ${GLOBAL_ADMIN_LABEL} access and remove them from this ${config.vocab.orgNoun} instead.`}
          </ErrorMessage>
        )}
        {serverError !== null && (
          <ErrorMessage testId={`${prefix}-error`}>{serverError}</ErrorMessage>
        )}

        {/* ── Remove from team ──────────────────────────────────────────────────────────────
            The destructive act, and the one the last-module copy above already points at. It sits
            APART from Save/Cancel and on the opposite side, because the row's other two controls
            answer "keep the changes I made" and this one answers a different question entirely —
            putting all three together invites the muscle memory that ends with the wrong one
            clicked. It renders only when the viewer may actually do it: no dead control. */}
        <div className="mt-8 flex flex-wrap items-center justify-between gap-8">
          <div>
            {canRemove && (
              <Button
                appearance="saas"
                tone="danger"
                size="md"
                disabled={saving}
                onClick={() => setConfirmingRemoval(true)}
                testId={`${prefix}-remove`}
              >
                Remove from team
              </Button>
            )}
          </div>
          <div className="flex items-center gap-8">
            <Dialog.Close
              disabled={saving}
              render={<Button appearance="saas" tone="secondary" size="md" />}
              {...testIdProp(`${prefix}-cancel`)}
            >
              Cancel
            </Dialog.Close>
            <Button
              appearance="saas"
              tone="primary"
              size="md"
              loading={saving}
              // Nothing changed ⇒ nothing to submit. The write is idempotent and would succeed happily,
              // but an empty save still reads as a decision in the record a Global Admin comes here to
              // read, so the control says there is nothing to do instead. A demotion with no app
              // turned on is not saveable at all (D-CPO-64's Also): the line above the apps says so.
              disabled={
                retryRefused ||
                onlyGlobalAdmin ||
                checkingRoster ||
                (demoting ? roleChange === null : patch === null)
              }
              onClick={submit}
              testId={`${prefix}-save`}
            >
              Save
            </Button>
          </div>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

interface ModuleSectionProps {
  config: OrgConsoleConfig;
  idPrefix: string;
  module: ModuleDraft;
  entry: OrgModuleCatalogRow | undefined;
  enabledForOrg: boolean;
  /** Offer "Customize permissions" — not while demoting, whose role change carries apps and levels. */
  customizeOffered: boolean;
  onChange: (next: Partial<ModuleDraft>) => void;
}

/**
 * One module's controls.
 *
 * ⚠ `has_access` AND `enabled_for_org` ARE ORTHOGONAL, and this section is where that shows. A module
 * the ORGANIZATION switched off is off for everyone including a Global Admin, but that is not a
 * statement about THIS person's grant — so the row says so and leaves every control live. Disabling
 * the controls instead would make an org-wide switch look like a per-person one, and would make the
 * grant unreachable from here: nobody could pre-set who gets the module before switching it back on.
 */
function ModuleSection({
  config,
  idPrefix,
  module,
  entry,
  enabledForOrg,
  customizeOffered,
  onChange,
}: ModuleSectionProps) {
  const customizable = customizeOffered && isCustomizable(entry);
  const memberKeys = keysAtLevel(entry, LEVEL_MEMBER);
  const ownerKeys = keysAtLevel(entry, LEVEL_OWNER);
  const label = config.vocab.moduleLabel(module.module);
  const selected = new Set(module.custom ?? []);

  const toggleKey = (key: string, checked: boolean) => {
    const next = new Set(module.custom ?? []);
    if (checked) next.add(key);
    else next.delete(key);
    onChange({ custom: [...next] });
  };

  return (
    <section className="rounded-lg border border-border-subtle p-16" {...testIdProp(idPrefix)}>
      <div className="flex items-start justify-between gap-16">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-8">
            <p className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">{label}</p>
            {!enabledForOrg && (
              <span {...testIdProp(`${idPrefix}-org-off`)}>
                <Badge tone="neutral">{offForOrgLabel(config.vocab.orgNoun)}</Badge>
              </span>
            )}
          </div>
          {!enabledForOrg && (
            <p className="mt-4 font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary">
              Nobody can use this app until it is switched back on under Apps. What you set here
              applies when it is.
            </p>
          )}
        </div>
        <Switch
          appearance="saas"
          checked={module.hasAccess}
          onCheckedChange={(checked) => onChange({ hasAccess: checked })}
          aria-label={`${label} access`}
          testId={`${idPrefix}-access`}
        />
      </div>

      {module.hasAccess && (
        <div className="mt-16 flex flex-col gap-12 border-t border-border-subtle pt-16">
          <Select
            appearance="saas"
            label="Level"
            value={module.tier}
            onValueChange={(value) =>
              onChange({ tier: value === ROLE_OWNER ? ROLE_OWNER : (ROLE_MEMBER as OrgTier) })
            }
            options={[
              { value: ROLE_MEMBER, label: MEMBER_LABEL },
              { value: ROLE_OWNER, label: ROLE_OWNER },
            ]}
            testId={`${idPrefix}-tier`}
          />
          {/* What the level gives (OM-94, round 4): the invite's words, so "everything that comes
              with their level" below names what that is. A custom set's own list replaces it. */}
          {module.custom === null && (
            <LevelGives
              config={config}
              module={module.module}
              tier={module.tier}
              entry={entry}
              testId={`${idPrefix}-level-gives`}
            />
          )}

          {customizable && (
            <>
              <div className="flex items-center justify-between gap-16">
                <div className="min-w-0">
                  <p className="font-sans text-s13 font-w600 tracking-givmo text-text-strong">
                    Customize permissions
                  </p>
                  <p className="mt-2 font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary">
                    Off means they get everything that comes with their level.
                  </p>
                </div>
                <Switch
                  appearance="saas"
                  checked={module.custom !== null}
                  // Turning it ON seeds the list from what they have RIGHT NOW, so the first thing a
                  // person sees is their current access — not an empty form that would silently
                  // revoke everything if they saved it. Turning it OFF emits the wire's own `null`,
                  // meaning "back to the level's defaults".
                  onCheckedChange={(checked) =>
                    onChange({ custom: checked ? tierDefaults(entry, module.tier) : null })
                  }
                  aria-label={`Customize ${label} permissions`}
                  testId={`${idPrefix}-customize`}
                />
              </div>

              {module.custom !== null && (
                <div className="flex flex-col gap-12" {...testIdProp(`${idPrefix}-permissions`)}>
                  <PermissionGroup
                    heading={`Comes with ${MEMBER_LABEL}`}
                    keys={memberKeys}
                    selected={selected}
                    onToggle={toggleKey}
                    idPrefix={idPrefix}
                    label={config.vocab.permissionLabel}
                  />
                  {/* Owner-level strings are rendered DISTINCTLY but are not forbidden: a custom set
                      may raise within the module's catalog, so one module-Owner power can be handed
                      to a module Member without promoting them. Marking them is what keeps that a
                      deliberate act rather than an accident. */}
                  <PermissionGroup
                    heading={`${ROLE_OWNER}-level`}
                    note={`Normally only for ${ROLE_OWNER}s of this app.`}
                    keys={ownerKeys}
                    selected={selected}
                    onToggle={toggleKey}
                    idPrefix={idPrefix}
                    label={config.vocab.permissionLabel}
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}

interface PermissionGroupProps {
  heading: string;
  note?: string;
  keys: readonly string[];
  selected: ReadonlySet<string>;
  onToggle: (key: string, checked: boolean) => void;
  idPrefix: string;
  label: (key: string) => string;
}

function PermissionGroup({
  heading,
  note,
  keys,
  selected,
  onToggle,
  idPrefix,
  label,
}: PermissionGroupProps) {
  if (keys.length === 0) return null;
  return (
    <div>
      <p className="font-sans text-s11 font-w600 uppercase tracking-wide text-text-tertiary">
        {heading}
      </p>
      {note !== undefined && (
        <p className="mt-2 font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary">
          {note}
        </p>
      )}
      <ul className="mt-8 flex flex-col gap-8">
        {keys.map((key) => (
          <li key={key} className="flex items-center gap-10">
            <Checkbox
              appearance="saas"
              checked={selected.has(key)}
              onCheckedChange={(checked) => onToggle(key, checked)}
              aria-label={label(key)}
              testId={`${idPrefix}-perm-${key}`}
            />
            <span className="font-sans text-s13 font-w400 tracking-givmo text-text-secondary">
              {label(key)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
